"use client";

import { Fragment, useMemo, useState } from "react";
import Link from "next/link";
import { SearchX } from "lucide-react";
import type { Tool } from "@/types";
import type { Category } from "@/types";
import { ToolCard } from "@/components/ui/tool-card";
import { EmptyState } from "@/components/ui/empty-state";
import { AdBanner } from "@/components/ads";
import { adSlots } from "@/lib/data/ads";
import { sortTools, TOOL_SORTS, type ToolSort } from "@/lib/sort-tools";

interface Props {
  tools: Tool[];
  categories: Category[];
}

export function ToolsListingClient({ tools, categories }: Props) {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<ToolSort>("popular");

  const filtered = useMemo(() => {
    let result = [...tools];
    if (selectedCategory) {
      result = result.filter((t) => t.category === selectedCategory);
    }
    if (query.trim()) {
      const q = query.toLowerCase().trim();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.slug.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          (t.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false)
      );
    }
    return sortTools(result, sort);
  }, [tools, query, selectedCategory, sort]);

  const totalCount = tools.length;
  const showingCount = filtered.length;

  return (
    <>
      <section className="container py-6 md:py-8">
        <div className="space-y-5">
          <div data-testid="tool-filter-toolbar" className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-sm)]">
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_200px_auto] lg:items-end">
              <div>
                <label htmlFor="filter-q" className="technical-label mb-1.5 block text-[var(--color-text-muted)]">
                  Search tools
                </label>
                <input
                  id="filter-q"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, format, or task"
                  className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-input-border,var(--color-border-strong))] bg-[var(--color-surface)] px-3.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                />
              </div>

              <div>
                <label htmlFor="filter-category" className="technical-label mb-1.5 block text-[var(--color-text-muted)]">
                  Category
                </label>
                <select
                  id="filter-category"
                  value={selectedCategory ?? ""}
                  onChange={(e) => setSelectedCategory(e.target.value || null)}
                  className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-input-border,var(--color-border-strong))] bg-[var(--color-surface)] px-3.5 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name} ({tools.filter((tool) => tool.category === category.name).length})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="filter-sort" className="technical-label mb-1.5 block text-[var(--color-text-muted)]">
                  Sort by
                </label>
                <select
                  id="filter-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as ToolSort)}
                  className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-input-border,var(--color-border-strong))] bg-[var(--color-surface)] px-3.5 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                >
                  {TOOL_SORTS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <Link href="/categories" className="inline-flex h-10 items-center justify-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-accent)] px-4 text-sm font-semibold text-[var(--color-accent)] transition-all hover:bg-[var(--color-accent-soft)]">
                Browse categories
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] pt-3">
              <p className="text-sm text-[var(--color-text-muted)]" aria-live="polite">
                {showingCount === totalCount ? `All ${totalCount} tools` : `${showingCount} of ${totalCount} tools${selectedCategory ? ` in ${selectedCategory}` : ""}${query ? ` for “${query}”` : ""}`}
              </p>
              {(query || selectedCategory) && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); setSelectedCategory(null); }}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-1.5 text-sm font-semibold text-[var(--color-accent)] transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              icon={<SearchX className="h-5 w-5" aria-hidden="true" />}
              title="No tools match your filters"
              description="Try another search term, or reset the category filter to see all tools."
              action={{
                label: "Clear filters",
                onClick: () => { setQuery(""); setSelectedCategory(null); },
              }}
            />
          ) : (
            <div data-testid="tool-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 *:min-w-0">
              {filtered.map((tool, index) => (
                <Fragment key={tool.slug}>
                  {index === Math.ceil(filtered.length / 2) && (
                    <AdBanner slot={adSlots.toolsMid} className="col-span-full" />
                  )}
                  <ToolCard tool={tool} className="min-w-0" />
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
